// app/providers.tsx
"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, Suspense } from "react";
import { usePostHog } from "posthog-js/react";

import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import type { Profile } from "../../models";
import {
  PostHogFlagsProvider,
  type PostHogFlags,
} from "./posthog-flags-provider";
import { ENV_VARS } from "../../env";

export const identifyUser = (profile: Profile) => {
  posthog.identify(profile.user.auth.provider_id, {
    email: profile?.user.email,
    name: profile?.user.name,
    internal_id: profile?.user.id,
  });
};

interface PostHogClientProviderProps {
  children: React.ReactNode;
  flags: PostHogFlags;
  distinctID: string;
}

export function PostHogClientProvider({
  children,
  flags,
  distinctID,
}: PostHogClientProviderProps) {
  useEffect(() => {
    if (ENV_VARS.VERCEL_ENV !== "production") {
      posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
        api_host: "/a",
        person_profiles: "identified_only", // or 'always' to create profiles for anonymous users as well
        capture_pageview: false, // Disable automatic pageview capture, as we capture manually
        bootstrap: {
          featureFlags: flags,
          distinctID: distinctID,
        },
      });
    }
  }, []);

  return (
    <PHProvider client={posthog}>
      <PostHogFlagsProvider flags={flags}>
        <SuspendedPostHogPageView />
        {children}
      </PostHogFlagsProvider>
    </PHProvider>
  );
}

// TODO: If this errors in prod, use https://github.com/PostHog/posthog-js/issues/872#issuecomment-1886994581
// Remove comment if after May 2025

function PostHogPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthog = usePostHog();

  // Track pageviews
  useEffect(() => {
    if (pathname && posthog) {
      let url = window.origin + pathname;
      if (searchParams.toString()) {
        url = `${url}?${searchParams.toString()}`;
      }

      posthog.capture("$pageview", { $current_url: url });
    }
  }, [pathname, searchParams, posthog]);

  return null;
}

// Wrap PostHogPageView in Suspense to avoid the useSearchParams usage above
// from de-opting the whole app into client-side rendering
// See: https://nextjs.org/docs/messages/deopted-into-client-rendering
function SuspendedPostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  );
}
